import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from 'api/v1/api';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';

export type TutorVkStatus = {
  vk_configured: boolean;
  vk_linked: boolean;
  vk_screen_name?: string | null;
  vk_can_message: boolean;
  vk_community_url: string;
};

export const TUTOR_VK_QUERY_KEY = ['tutor-vk'] as const;

export function useTutorVkStatus() {
  const { data: session } = useAuthSession();
  return useQuery({
    queryKey: TUTOR_VK_QUERY_KEY,
    enabled: isAuthSessionActive(session),
    queryFn: async () => {
      const response = await api.get<TutorVkStatus>('/tutors/me/vk', {
        skipGlobalErrorHandler: true,
      });
      return response.data;
    },
  });
}

function useTutorVkMutation<TBody>(path: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body?: TBody) => {
      const response = await api.post<TutorVkStatus>(path, body ?? {}, {
        skipGlobalErrorHandler: true,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(TUTOR_VK_QUERY_KEY, data);
    },
  });
}

export function useLinkTutorVk() {
  return useTutorVkMutation<{ profile: string }>('/tutors/me/vk/link');
}

export function useUnlinkTutorVk() {
  return useTutorVkMutation('/tutors/me/vk/unlink');
}

export function useRefreshTutorVk() {
  return useTutorVkMutation('/tutors/me/vk/refresh');
}

export function useTestTutorVk() {
  return useTutorVkMutation('/tutors/me/vk/test');
}
